import type { ConnectionView } from '@/lib/connections';
import { Modal } from '@/components/Modal';
import { Button, Card, Eyebrow } from '@/components/ui';

/**
 * The consent explainer, shown before anyone leaves for the provider.
 *
 * Each permission with a plain reason, and three facts stated outright: consent
 * is organisation-wide, it is granted by an administrator, and it is revocable
 * from either side. Those three are what an IT admin will otherwise stop and
 * ask about halfway through, on the provider's own screen, with no way to find out.
 *
 * It also says what we will NOT do. That is not reassurance padding — "we never
 * delete a meeting someone in your organisation booked" is a real guarantee
 * this system keeps, and the person approving it deserves to see it before
 * approving rather than after.
 *
 * A MODAL RATHER THAN ITS OWN SCREEN. It used to replace the Connections page,
 * which read as navigation: the connection cards vanished, so the choice looked
 * already made and "Back to connections" was the only clue it was not. Over the
 * cards it is plainly a step you are in the middle of, and what you were
 * looking at is still behind it. The body scrolls and the buttons stay pinned —
 * this content is taller than a laptop viewport on every provider, and a
 * Continue button below the fold is one nobody finds.
 */
export function ConsentExplainer({
  open,
  connection,
  busy,
  onCancel,
  onContinue,
}: {
  open: boolean;
  connection: ConnectionView | null;
  busy: boolean;
  onCancel: () => void;
  onContinue: () => void;
}) {
  if (!open || !connection) return null;

  return (
    <Modal
      open
      maxWidth={720}
      eyebrow="Before you continue"
      title={`Connect ${connection.display_name}`}
      subtitle={`You are about to grant ZenSpace access to your organisation's ${connection.display_name} account. Here is exactly what that means.`}
      busy={busy}
      closeLabel="Back to connections"
      onClose={onCancel}
      footer={
        <div className="flex flex-wrap items-center gap-[14px]">
          <Button tone="primary" busy={busy} onClick={onContinue}>
            Continue to {connection.display_name}
          </Button>
          <Button tone="quiet" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      }
    >
      <Card>
        <Eyebrow>What we will be able to do</Eyebrow>
        <ul className="mt-[14px] mb-0 flex list-none flex-col gap-[20px] p-0">
          {connection.requested_access.map((access) => (
            <li key={access.scope} className="flex gap-[14px]">
              <span
                aria-hidden="true"
                className="mt-[6px] h-[8px] w-[8px] shrink-0 rounded-[--radius-pill] bg-green"
              />
              <div>
                <p className="m-0 text-lead text-ink">{access.reason}</p>
                {/* The raw scope or permission, for the IT admin who wants to
                    check it against what the provider is about to show them. */}
                <p className="m-0 mt-[4px] font-mono text-micro break-all text-muted">
                  {access.scope}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="mt-[20px]">
        <Eyebrow>What we will never do</Eyebrow>
        <ul className="mt-[14px] mb-0 flex list-none flex-col gap-[14px] p-0">
          {[
            'Delete or change a meeting that someone in your organisation booked. We only ever remove events that we created ourselves.',
            'Create, rename or delete anything in your directory — your rooms, buildings and people are yours alone.',
            'Read the contents of meetings. We record only times, room names and identifiers — never titles, attendees or descriptions.',
          ].map((promise) => (
            <li key={promise} className="flex gap-[14px] text-lead text-ink">
              <span aria-hidden="true" className="mt-[2px] shrink-0 text-brand">
                ✓
              </span>
              {promise}
            </li>
          ))}
        </ul>
      </Card>

      <Card className="mt-[20px]">
        <Eyebrow>How this consent works</Eyebrow>
        <dl className="mt-[14px] mb-0 flex flex-col gap-[14px]">
          <div>
            <dt className="m-0 text-lead font-semibold text-ink">
              It covers your whole organisation
            </dt>
            <dd className="m-0 mt-[4px] text-body text-muted">
              This is granted once for the organisation, not per person. Nobody else will be asked
              to approve anything.
            </dd>
          </div>
          <div>
            <dt className="m-0 text-lead font-semibold text-ink">
              It has to be granted by an administrator
            </dt>
            <dd className="m-0 mt-[4px] text-body text-muted">
              The next screen is {connection.display_name}&apos;s own. Sign in there with an
              account that administers your {connection.display_name} — a personal account will not
              be able to see your rooms.
            </dd>
          </div>
          <div>
            <dt className="m-0 text-lead font-semibold text-ink">
              You can withdraw it at any time, from either side
            </dt>
            <dd className="m-0 mt-[4px] text-body text-muted">
              Disconnect here, or remove ZenSpace from your {connection.display_name} admin console.
              Either works, and bookings already in your calendar stay exactly as they are.
            </dd>
          </div>
        </dl>
      </Card>

      <p className="mt-[20px] mb-0 text-body text-muted">
        After you grant access we will add a short test booking to one of your rooms and remove it
        again, to be certain bookings will actually stick. A calendar we can read but not book
        looks fine until the first real booking quietly fails, so we check properly rather than
        assuming.
      </p>
    </Modal>
  );
}
